import usePingApi from 'src/api/usePingApi';
import { useConnectionStore } from 'src/context/ConnectionContext';
import { OfflineLevelEnum } from 'src/enums/offlineLevelEnum';
import { PingTimeBM } from 'src/types/BusinessModel/PingTimeBM';
import useSyncOfflineData from './useSyncOfflineData';
import { getDbMetaDataForSync } from 'src/api/offline/offlineApi';
import { useEffect, useState } from 'react';
import useTriggerReleaseVersionCheck from 'src/utils/useTriggerReleaseVersionCheck';

const SYNC_CHECK_INTERVAL_MS = 300_000; // 5 minutes
let lastSyncCheckTimestamp = 0;

const calculateLatency = (previousSentTimestamp: number, receivedTimestamp: number): number => {
    if (receivedTimestamp !== -1 && previousSentTimestamp !== -1) {
        return receivedTimestamp - previousSentTimestamp;
    }
    return -1;
};

const useCheckConnection = () => {
    const DEFAULT_PING_REFRESH_MS = 60000;
    const FREQUENT_PING_REFRESH_MS = 1000;
    const LIMIT_NUM_ATTEMPTS_FOR_SWITCH = 3;
    const LATENCY_LIMIT_MS = 5000;
    // OPTIMIZATION: Delay initial ping check to avoid network overhead during page load
    const INITIAL_PING_DELAY_MS = 5000;

    let pingRefreshMS = DEFAULT_PING_REFRESH_MS;
    let numberOfAttempts = 0;
    let _offlineLevel: OfflineLevelEnum;

    const { setOfflineLevel, isOfflineSimulated } = useConnectionStore();
    const { checkPingTime } = usePingApi();
    const { syncOfflineData, resetSyncBackoff } = useSyncOfflineData();
    const { releaseVersionCheck } = useTriggerReleaseVersionCheck();
    const [offlineSinceDateTime, setOfflineSinceDateTime] = useState<number | null>(null);

    useEffect(() => {
        const _offlineSinceDateTime = localStorage.getItem('offlineSinceDateTime');
        if (_offlineSinceDateTime) {
            setOfflineSinceDateTime(+_offlineSinceDateTime);
        }
    }, []);

    const handleBadConnection = () => {
        if (numberOfAttempts <= LIMIT_NUM_ATTEMPTS_FOR_SWITCH) {
            if (_offlineLevel !== OfflineLevelEnum.BadConnectionDetected) {
                setOfflineLevel(OfflineLevelEnum.BadConnectionDetected);
            }
            _offlineLevel = OfflineLevelEnum.BadConnectionDetected;

            pingRefreshMS = FREQUENT_PING_REFRESH_MS;
            numberOfAttempts++;
        } else {
            if (_offlineLevel !== OfflineLevelEnum.Offline) {
                setOfflineLevel(OfflineLevelEnum.Offline);
            }
            _offlineLevel = OfflineLevelEnum.Offline;

            pingRefreshMS = DEFAULT_PING_REFRESH_MS;
            numberOfAttempts = 0;
            if (!offlineSinceDateTime) {
                const _offlineSinceDateTime = new Date().getTime();
                setOfflineSinceDateTime(_offlineSinceDateTime);
                localStorage.setItem('offlineSinceDateTime', `${_offlineSinceDateTime}`);
            }
        }
    };

    const handleGoodConnection = () => {
        if (numberOfAttempts <= LIMIT_NUM_ATTEMPTS_FOR_SWITCH) {
            pingRefreshMS = FREQUENT_PING_REFRESH_MS;
            numberOfAttempts++;
        } else if (numberOfAttempts > LIMIT_NUM_ATTEMPTS_FOR_SWITCH) {
            setOfflineLevel(OfflineLevelEnum.Online);
            _offlineLevel = OfflineLevelEnum.Online;

            pingRefreshMS = DEFAULT_PING_REFRESH_MS;
            numberOfAttempts = 0;

            if (offlineSinceDateTime) {
                setOfflineSinceDateTime(null);
                localStorage.removeItem('offlineSinceDateTime');
            }

            resetSyncBackoff();
            syncOfflineData();
        }
    };

    const updateLatency = (updatedPingTime, latency, networkError) => {
        if (!updatedPingTime && !networkError) return;
        const isBadConnection = latency > LATENCY_LIMIT_MS || networkError;
        const isGoodConnection = latency <= LATENCY_LIMIT_MS && !networkError;

        if (isBadConnection) {
            handleBadConnection();
        } else if (isGoodConnection && _offlineLevel !== OfflineLevelEnum.Online) {
            handleGoodConnection();
        }

        // Periodic check: if online and ping succeeded, check IndexedDB for orphaned items
        if (isGoodConnection && _offlineLevel === OfflineLevelEnum.Online) {
            const now = Date.now();
            if (now - lastSyncCheckTimestamp >= SYNC_CHECK_INTERVAL_MS) {
                lastSyncCheckTimestamp = now;
                // Fire-and-forget — don't block the ping cycle
                getDbMetaDataForSync().then((meta) => {
                    if (meta.itemCount > 0) {
                        syncOfflineData();
                    }
                }).catch(() => {
                    // Silently ignore — don't disrupt ping cycle
                });
            }
        }
    };

    const checkLatency = async (imoNumber: number, previousSentTimestamp: number, receivedTimestamp: number) => {
        const latency = calculateLatency(previousSentTimestamp, receivedTimestamp);
        let networkError = false;

        const sentTimestamp = new Date().getTime();
        const checkPingResponse = await checkPingTime(imoNumber.toString(), previousSentTimestamp, latency, offlineSinceDateTime);
        const _isOfflineSimulated = await isOfflineSimulated();
        if (!_isOfflineSimulated && checkPingResponse?.status === 200) {
            const updatedPingTime = checkPingResponse.data as PingTimeBM;
            receivedTimestamp = new Date().getTime();
            updateLatency(updatedPingTime, latency, false);

            // Version check from ping response (network-fresh, never SW-cached)
            const apiVersion = checkPingResponse?.headers?.['release-version'];
            if (apiVersion) {
                releaseVersionCheck(apiVersion);
            }

            if (updatedPingTime && latency > 0) {
                previousSentTimestamp = sentTimestamp;
            }
        } else if (checkPingResponse?.status !== 200 || checkPingResponse?.message === 'Network Error' || _isOfflineSimulated) {
            networkError = true;
            updateLatency(null, latency, networkError);
        }

        setTimeout(async () => {
            checkLatency(imoNumber, sentTimestamp, receivedTimestamp);
        }, pingRefreshMS);
    };

    // OPTIMIZATION: Start ping checking with initial delay to avoid network overhead during page load
    const startCheckLatency = (imoNumber: number) => {
        setTimeout(() => {
            checkLatency(imoNumber, -1, -1);
        }, INITIAL_PING_DELAY_MS);
    };

    return { checkLatency, startCheckLatency };
};

export default useCheckConnection;
